<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'
import { useKcAuthSnapshot } from '@/composables/useKcAuthSnapshot'
import logoEveGuest from '@/assets/logos/logo-guest.svg'
import logoEveGuestMobile from '@/assets/logos/logo-guest-mobile.svg'
import { useGuestStore } from '@/modules/guest/store'

const guestStore = useGuestStore()

const { isAuthenticated } = useKcAuthSnapshot()
const { t } = useI18n()
const { mobile } = useDisplay()
const router = useRouter()
const route = useRoute()

const PRIVATE_BASE =
  (import.meta.env.VITE_PRIVATE_BASE_URL as string | undefined) ??
  window.location.origin

const unreadCount = computed(() => guestStore.unreadNotificationsCount)
const hasUnread = computed(() => guestStore.hasUnreadNotifications)

function goGuestHome() {
  if (router.hasRoute('guest')) router.push({ name: 'guest' })
  else router.push('/guest')
}

function goNotifications() {
  router.push('/guest/notifications')
}

function getEventId(): string {
  const fromParams = route.params?.eventId ? String(route.params.eventId) : ''
  if (fromParams) return fromParams

  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const parts = path.split('/').filter(Boolean)
  return parts[parts.length - 1] || ''
}

function handleSignIn() {
  const eventId = getEventId()
  const privatePath = eventId
    ? `/guest/event/${encodeURIComponent(eventId)}`
    : '/guest'

  const base = (PRIVATE_BASE || window.location.origin).replace(/\/$/, '')
  window.location.assign(base + privatePath)
}

onMounted(async () => {
  if (isAuthenticated.value) {
    await guestStore.getNotifications()
  }
})
</script>

<template>
  <v-app-bar app color="grey-darken-4" elevation="2" class="px-4">
    <div class="nav-logo h-100" role="button" tabindex="0" @click="goGuestHome">
      <v-img
        :src="mobile ? logoEveGuestMobile : logoEveGuest"
        alt="EVE Guest"
        height="40"
        width="auto"
        :eager="true"
      />
    </div>

    <v-spacer />

    <v-btn
      v-if="!isAuthenticated"
      id="public-login-button"
      variant="text"
      class="text-white"
      prepend-icon="mdi-account"
      @click="handleSignIn"
    >
      {{ t('general.signIn') }}
    </v-btn>

    <template v-else>
      <v-btn icon variant="text" class="mr-2" @click="goNotifications">
        <v-badge
          :model-value="hasUnread"
          :content="unreadCount"
          color="error"
          location="top end"
        >
          <v-icon>mdi-bell</v-icon>
        </v-badge>
      </v-btn>

      <v-btn
        id="public-go-to-app"
        color="primary"
        variant="flat"
        prepend-icon="mdi-arrow-right-circle"
        @click="goGuestHome"
      >
        {{ t('guest.home.title') }}
      </v-btn>
    </template>
  </v-app-bar>
</template>

<style scoped>
.nav-logo {
  display: flex;
  align-items: center;
  padding-left: 16px;
  margin-left: 8px;
}
</style>
